/**
 * SupportPage
 * Página principal del módulo de soporte técnico.
 * Solo accesible para usuarios con códigos en REACT_APP_SUPPORT_EMPLOYEES.
 */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Button,
  Stack,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import {
  Terminal as TerminalIcon,
  Api as ApiIcon,
  Download as DownloadIcon,
  DeleteSweep as ClearIcon,
  BugReport as BugIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useDebug } from "../../../context/DebugContext";
import useDebugLogger from "../../../hooks/useDebugLogger";
import ConsoleLogsPanel from "../components/ConsoleLogsPanel";
import ApiLogsPanel from "../components/ApiLogsPanel";
import ChangelogPanel from "../components/ChangelogPanel";
import { APP_VERSION } from "../../../config/changelog";

const SupportPage = () => {
  const { debugEnabled, toggleDebug, isSupportUser } = useDebug();
  const {
    consoleLogs,
    apiLogs,
    clearLogs,
    clearConsoleLogs,
    clearApiLogs,
    exportLogs,
  } = useDebugLogger();
  const [activeTab, setActiveTab] = useState(0);

  // Verificación de seguridad adicional
  if (!isSupportUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No tienes permisos para acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Determinar si mostrar controles de debug (no en pestaña changelog)
  const showDebugControls = activeTab !== 2;

  return (
    <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <BugIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" component="h1">
                  Soporte Tecnico
                </Typography>
                <Chip
                  label={`v${APP_VERSION}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Panel de debugging, monitoreo y versiones
              </Typography>
            </Box>
          </Stack>

          {showDebugControls && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={debugEnabled}
                    onChange={toggleDebug}
                    color="primary"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>Modo Debug</span>
                    <Chip
                      size="small"
                      label={debugEnabled ? "ON" : "OFF"}
                      color={debugEnabled ? "success" : "default"}
                    />
                  </Stack>
                }
              />
            </Stack>
          )}
        </Stack>

        {debugEnabled && showDebugControls && (
          <Alert severity="info" sx={{ mt: 2 }}>
            El modo debug esta activo. Se estan capturando logs de consola y llamadas API.
          </Alert>
        )}
      </Paper>

      {/* Stats - Solo mostrar en pestañas de debug */}
      {showDebugControls && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack
            direction="row"
            spacing={3}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Console Logs
              </Typography>
              <Typography variant="h6">{consoleLogs.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                API Calls
              </Typography>
              <Typography variant="h6">{apiLogs.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Errores
              </Typography>
              <Typography variant="h6" color="error.main">
                {consoleLogs.filter((log) => log.level === "error").length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                API Errors
              </Typography>
              <Typography variant="h6" color="error.main">
                {apiLogs.filter((log) => log.type === "response_error").length}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Actions - Solo mostrar en pestañas de debug */}
      {showDebugControls && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportLogs}
              disabled={consoleLogs.length === 0 && apiLogs.length === 0}
            >
              Exportar Logs
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ClearIcon />}
              onClick={clearLogs}
              disabled={consoleLogs.length === 0 && apiLogs.length === 0}
            >
              Limpiar Todo
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={clearConsoleLogs}
              disabled={consoleLogs.length === 0}
            >
              Limpiar Consola
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={clearApiLogs}
              disabled={apiLogs.length === 0}
            >
              Limpiar API
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<TerminalIcon />}
            iconPosition="start"
            label={`Console (${consoleLogs.length})`}
          />
          <Tab
            icon={<ApiIcon />}
            iconPosition="start"
            label={`API Calls (${apiLogs.length})`}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Changelog"
          />
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto", p: 0 }}>
          {activeTab === 0 && <ConsoleLogsPanel logs={consoleLogs} />}
          {activeTab === 1 && <ApiLogsPanel logs={apiLogs} />}
          {activeTab === 2 && <ChangelogPanel />}
        </Box>
      </Paper>
    </Box>
  );
};

export default SupportPage;
